import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Smartphone, X, Download } from "lucide-react";
import { useInstallState, promptInstall, openInstall } from "@/lib/install";

const HIDE_KEY = "dmv_appbar_hidden";

/**
 * Slim "Get the app" strip for Home and Listings.
 * Disappears for good once the app is installed (or when opened from the home screen);
 * the × hides it for this visit only. The footer "Get the app" button always stays.
 */
export default function AppBar() {
  const { canPrompt, installed, ios, mobile } = useInstallState();
  const [hidden, setHidden] = useState(() => { try { return sessionStorage.getItem(HIDE_KEY) === "1"; } catch { return false; } });
  const show = !installed && !hidden && (canPrompt || ios || mobile);

  const close = () => { setHidden(true); try { sessionStorage.setItem(HIDE_KEY, "1"); } catch {} };
  const install = async () => { if (canPrompt) await promptInstall(); else openInstall(); };

  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden" data-testid="app-bar">
          <div className="bg-gradient-to-r from-[#4D0E12] via-[#8C1C22] to-[#A11E23] text-white">
            <div className="max-w-[1400px] mx-auto px-3 sm:px-6 md:px-10 py-2 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><Smartphone className="w-4 h-4 text-[color:var(--gold-2)]"/></span>
              <div className="flex-1 min-w-0 text-[13px] sm:text-[14px] leading-tight">
                <b className="font-semibold">Get the DartMyVenue app</b>
                <span className="hidden sm:inline text-white/75"> · full-screen, faster, one tap from your home screen</span>
              </div>
              <button onClick={install} className="press shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--gold-2)] text-[color:var(--maroon-2)] px-3.5 py-1.5 text-[13px] font-bold hover:brightness-105" data-testid="app-bar-install">
                <Download className="w-3.5 h-3.5"/> {ios && !canPrompt ? "How to add" : "Install"}
              </button>
              <button onClick={close} className="shrink-0 p-1.5 rounded-full hover:bg-white/10 text-white/80" aria-label="Hide" data-testid="app-bar-close"><X className="w-4 h-4"/></button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
